import { addDaysISO, normalizeISODate } from './time';
import type { HijriDate, IslamicEvent, PrayerDay } from './types';

const HIJRI_FORMATTER = new Intl.DateTimeFormat('en-u-ca-islamic-umalqura-nu-latn', {
  timeZone: 'UTC',
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric'
});

const GREGORIAN_FORMATTER = new Intl.DateTimeFormat('en-CA-u-ca-gregory-nu-latn', {
  timeZone: 'UTC',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
});

const hijriCache = new Map<string, HijriDate>();

type EventDef = {
  name: string;
  day: number;
  month: number;
  category: string;
  importanceRank: number;
  durationDays: number;
};

const EVENT_DEFS: EventDef[] = [
  { name: 'Islamic New Year (1 Muharram)', day: 1, month: 1, category: 'Month/Year', importanceRank: 60, durationDays: 1 },
  { name: 'Day of Ashura (10 Muharram)', day: 10, month: 1, category: 'Fasting/History', importanceRank: 70, durationDays: 1 },
  { name: 'Start of Ramadan (1 Ramadan)', day: 1, month: 9, category: 'Ramadan', importanceRank: 95, durationDays: 1 },
  { name: 'Laylat al-Qadr (estimated, last 10 nights)', day: 27, month: 9, category: 'Ramadan/Night', importanceRank: 99, durationDays: 3 },
  { name: 'Eid al-Fitr (1 Shawwal)', day: 1, month: 10, category: 'Eid', importanceRank: 100, durationDays: 1 },
  { name: 'Day of Arafah (9 Dhu al-Hijjah)', day: 9, month: 12, category: 'Hajj/Fasting', importanceRank: 92, durationDays: 1 },
  { name: 'Eid al-Adha (10 Dhu al-Hijjah)', day: 10, month: 12, category: 'Eid', importanceRank: 100, durationDays: 1 },
  { name: 'Days of Tashreeq (11-13 Dhu al-Hijjah)', day: 11, month: 12, category: 'Hajj', importanceRank: 80, durationDays: 3 }
];

function isoToUtcDate(iso: string): Date {
  const normalized = normalizeISODate(iso, iso);
  const [year, month, day] = normalized.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

function dateToIsoUTC(date: Date): string {
  const parts = GREGORIAN_FORMATTER.formatToParts(date);
  const year = parts.find((part) => part.type === 'year')?.value ?? '0000';
  const month = parts.find((part) => part.type === 'month')?.value ?? '00';
  const day = parts.find((part) => part.type === 'day')?.value ?? '00';
  return `${year}-${month}-${day}`;
}

function partsToHijri(parts: Intl.DateTimeFormatPart[]): HijriDate {
  const weekdayName = parts.find((part) => part.type === 'weekday')?.value;
  const monthName = parts.find((part) => part.type === 'month')?.value ?? 'Hijri';
  const day = Number(parts.find((part) => part.type === 'day')?.value ?? '0');
  const year = Number(parts.find((part) => part.type === 'year')?.value ?? '0');
  const month = monthNumberFromName(monthName);

  return {
    day,
    month,
    monthName,
    year,
    weekdayName
  };
}

function monthNumberFromName(monthName: string): number {
  const normalized = monthName.trim().toLowerCase();
  const names = [
    'muharram',
    'safar',
    'rabiʻ i',
    'rabiʻ ii',
    'jumada i',
    'jumada ii',
    'rajab',
    'shaʻban',
    'ramadan',
    'shawwal',
    'dhuʻl-qiʻdah',
    'dhuʻl-hijjah'
  ];
  const alt: Record<string, number> = {
    'rabi i': 3,
    'rabi ii': 4,
    'jumada i': 5,
    'jumada ii': 6,
    'shaaban': 8,
    'shaʻban': 8,
    'sha’ban': 8,
    'dhul-qidah': 11,
    'dhu al-qidah': 11,
    'dhu al-hijjah': 12,
    'dhul-hijjah': 12,
    'dhuʻl-qiʻdah': 11,
    'dhuʻl-hijjah': 12
  };
  const fromList = names.findIndex((name) => name === normalized);
  if (fromList >= 0) return fromList + 1;
  return alt[normalized] ?? 0;
}

export function gregorianToHijri(dateISO: string, adjustment = 0): HijriDate {
  const normalized = normalizeISODate(dateISO, dateISO);
  const cacheKey = `${normalized}|${adjustment}`;
  const cached = hijriCache.get(cacheKey);
  if (cached) return cached;

  const shiftedISO = adjustment === 0 ? normalized : addDaysISO(normalized, adjustment);
  const parts = HIJRI_FORMATTER.formatToParts(isoToUtcDate(shiftedISO));
  const hijri = partsToHijri(parts);
  hijriCache.set(cacheKey, hijri);
  return hijri;
}

export function applyHijriAdjustmentToPrayerDays(days: PrayerDay[], adjustment: number): PrayerDay[] {
  return days.map((day) => ({
    ...day,
    dateHijri: gregorianToHijri(day.dateGregorianISO, adjustment),
    meta: { ...day.meta, hijriAdjustment: adjustment }
  }));
}

export function formatHijriLabel(hijri: HijriDate | null | undefined): string {
  if (!hijri) return '—';
  return `${hijri.day} ${hijri.monthName} ${hijri.year}`;
}

function matchesEvent(hijri: HijriDate, eventDef: EventDef): boolean {
  return hijri.month === eventDef.month && hijri.day === eventDef.day;
}

export function deriveUpcomingIslamicEvents(fromISO: string, adjustment: number, limit = 10, horizonDays = 430): IslamicEvent[] {
  const startISO = normalizeISODate(fromISO, fromISO);
  const results: IslamicEvent[] = [];
  const seen = new Set<string>();

  for (let offset = 0; offset <= horizonDays && results.length < limit; offset += 1) {
    const dateISO = addDaysISO(startISO, offset);
    const hijri = gregorianToHijri(dateISO, adjustment);

    for (const eventDef of EVENT_DEFS) {
      if (!matchesEvent(hijri, eventDef)) continue;
      const key = `${eventDef.name}|${dateISO}`;
      if (seen.has(key)) continue;
      seen.add(key);
      results.push({
        name: eventDef.name,
        hijriDate: { day: eventDef.day, month: eventDef.month },
        approxGregorianRange: {
          startISO: dateISO,
          endISO: addDaysISO(dateISO, eventDef.durationDays - 1)
        },
        category: eventDef.category,
        importanceRank: eventDef.importanceRank
      });
    }
  }

  return results.sort((a, b) => {
    if (a.approxGregorianRange.startISO !== b.approxGregorianRange.startISO) {
      return a.approxGregorianRange.startISO.localeCompare(b.approxGregorianRange.startISO);
    }
    return b.importanceRank - a.importanceRank;
  }).slice(0, limit);
}

export function findGregorianForHijri(target: { day: number; month: number; year: number }, adjustment = 0): string | null {
  const guessedGregorianYear = Math.round((target.year - 1) * 0.970224 + 622);
  const start = new Date(Date.UTC(guessedGregorianYear - 1, 0, 1));
  const end = new Date(Date.UTC(guessedGregorianYear + 1, 11, 31));

  for (let cursor = new Date(start); cursor <= end; cursor.setUTCDate(cursor.getUTCDate() + 1)) {
    const dateISO = dateToIsoUTC(cursor);
    const hijri = gregorianToHijri(dateISO, adjustment);
    if (hijri.day === target.day && hijri.month === target.month && hijri.year === target.year) {
      return dateISO;
    }
  }

  return null;
}
